<template>
  <div class="mt-2">
    <WarningDisable permission="screenshot" />
    <div class="grow">
      <div class="text-base-content/80 mb-2 text-xl font-bold">
        Lịch sử chụp màn hình
      </div>
      <Loading v-if="isLoading" />
      <template v-else-if="totalFiltered > 0">
        <div
          class="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
        >
          <ScreenshotCard
            v-for="(sc, index) in screenshots"
            :key="sc.id"
            :created_at="sc.created"
            :url="`/api/v1/view/screenshot/${sc.path}`"
            @click="openSlider(index)"
            class="cursor-pointer"
          />
        </div>
        <div v-if="isFetchingNextPage" class="flex justify-center py-4">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
        <div v-else-if="hasNextPage" class="flex justify-center py-4">
          <button @click="() => fetchNextPage()" class="btn btn-primary">
            Tải thêm
          </button>
        </div>
      </template>
      <NotFound v-else-if="totalFiltered === 0" />
    </div>
  </div>
  <ImageSlider
    ref="imgSlider"
    :images="sliderImages"
    :initial-index="currentImageIndex"
    :total-length="totalFiltered"
    @near-end="handleNearEnd"
  />
</template>

<script setup lang="ts">
import Loading from "@/components/LoadingTemplate.vue";
import NotFound from "@/components/NotFound.vue";
import { router } from "@/router/router";
import { useInfiniteScroll } from "@vueuse/core";
import dayjs from "dayjs";
import { computed, onMounted, reactive, ref } from "vue";
import WarningDisable from "../WarningDisable.vue";
import type { screenshot } from "./index";
import ScreenshotCard from "./ScreenshotCard.vue";
import { useScreenshotsInfiniteQuery } from "./useScreenshot";
import ImageSlider, { type SliderImage } from "@/components/ImageSlider.vue";

// Reactive filters based on route params
const filters = reactive({
  agent_id: router.currentRoute.value.params.client_id as string,
  size: 20,
  keyword: "",
});

// Watch route changes to update filters
router.afterEach((to) => {
  if (to.name === "ClientScreenshot") {
    filters.agent_id = to.params.client_id as string;
  }
});

// Use Vue Query with infinite scroll
const {
  screenshots,
  totalFiltered,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
} = useScreenshotsInfiniteQuery(filters);

const currentImageIndex = ref(0);
const imgSlider = ref<InstanceType<typeof ImageSlider> | null>(null);

// Setup infinite scrolling on the window (page scroll) within onMounted,
// as the main content area becomes available after component mount.
onMounted(() => {
  useInfiniteScroll(
    document.querySelector("main"),
    () => {
      if (hasNextPage.value && !isFetchingNextPage.value) {
        fetchNextPage();
      }
    },
    { distance: 100 },
  );
});

// Handle when user navigates near the end in image slider
const handleNearEnd = () => {
  if (hasNextPage.value && !isFetchingNextPage.value) {
    fetchNextPage();
  }
};

// Convert screenshots to slider images format
const sliderImages = computed<SliderImage[]>(() => {
  return screenshots.value.map((sc: screenshot) => ({
    url: `/api/v1/view/screenshot/${sc.path}`,
    alt: sc.path,
    title: dayjs(sc.created).format("DD/MM/YYYY HH:mm:ss"),
  }));
});

const openSlider = (index: number) => {
  currentImageIndex.value = index;
  imgSlider.value?.setIndex(index);
  imgSlider.value?.openModal();
};
</script>
